/**
 * OrgSwitcher - the tenant selector.
 *
 * The default export is the sidebar row used on mobile (the nav rail owns
 * the desktop switcher — see `NavRailOrgSwitcher`); both render from the
 * shared `useOrgSelection` + `OrgMenuItems` here. Selecting an org navigates
 * to `/$org` (the org slug) and records the choice (DB `lastChosenOrgId`
 * when the session has a platform user, localStorage always) so `/` lands
 * there next time.
 */
import { useNavigate } from "@tanstack/react-router";
import { ArrowUpRightIcon, Building2Icon, ChevronDownIcon } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";

import { orgUrlSegment } from "../orgRouting";
import {
  persistLastPickedOrg,
  useActiveOrg,
  useOrgs,
  type ElsewhereOrgView,
  type OrgView,
} from "../state/orgs";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "./ui/menu";
import { SidebarMenuButton } from "./ui/sidebar";
import { useTheme } from "../hooks/useTheme";
import { organizationMarkUrl } from "~/lib/organizationFiles";

/**
 * Where a mark sits. `page` follows the app theme. `frame` is the shell's
 * rail and top bar: dark in dark mode, and dark in the default light theme
 * too, whose frame is the Amoreg grey (index.css, "Default light: the
 * frame"). A custom theme paints its own frame, light or dark with the theme.
 */
export type OrgMarkSurface = "page" | "frame";

/** Whether the mark on `surface` sits on a dark background. */
export function orgMarkOnDark(input: {
  readonly surface: OrgMarkSurface;
  readonly resolvedTheme: "light" | "dark";
  /** `data-theme-id` on the root: set while a custom theme is applied. */
  readonly customThemeId: string | undefined;
}): boolean {
  if (input.resolvedTheme === "dark") return true;
  return input.surface === "frame" && !input.customThemeId;
}

const activeCustomThemeId = (): string | undefined =>
  typeof document === "undefined"
    ? undefined
    : document.documentElement?.dataset.themeId || undefined;

/**
 * The org's mark for the surface it sits on: `useTheme().resolvedTheme` (and,
 * on the shell frame, whether a custom theme is applied) picks the URL, so one
 * <img> renders and the dark asset is chosen the same way every other themed
 * surface chooses. Falls back to the light mark when the org has no dark one,
 * and to `fallback` when it has no mark at all or the chosen image fails to
 * load. Every surface showing the org mark goes through this.
 */
export function OrgLogoImage({
  org,
  className,
  fallback,
  surface = "page",
}: {
  readonly org: OrgView | null;
  readonly className: string;
  readonly fallback: ReactNode;
  readonly surface?: OrgMarkSurface;
}) {
  const { resolvedTheme } = useTheme();
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  const darkUrl = org?.logoDarkUrl ?? null;
  // Read during render: a theme change re-renders through useTheme after the
  // root's theme id is applied.
  const onDark = orgMarkOnDark({ surface, resolvedTheme, customThemeId: activeCustomThemeId() });
  // A private mark (IOM FSA) is a route on the server, resolved to its origin.
  const url = organizationMarkUrl(onDark && darkUrl ? darkUrl : (org?.logoUrl ?? null));
  if (url === null || url === brokenUrl) {
    return <>{fallback}</>;
  }
  return (
    <img
      src={url}
      alt=""
      aria-hidden="true"
      className={className}
      onError={() => setBrokenUrl(url)}
    />
  );
}

export function OrgLogo({
  org,
  className,
  surface = "page",
}: {
  readonly org: OrgView | null;
  readonly className?: string;
  readonly surface?: OrgMarkSurface;
}) {
  const size = className ?? "size-4";
  return (
    <OrgLogoImage
      org={org}
      surface={surface}
      className={`shrink-0 rounded-[4px] object-cover ${size}`}
      fallback={
        org ? (
          <span
            aria-hidden="true"
            className={`flex shrink-0 items-center justify-center rounded-[4px] bg-sidebar-control-surface text-[9px] font-semibold uppercase text-sidebar-muted-foreground ${size}`}
          >
            {org.name.slice(0, 1)}
          </span>
        ) : (
          <Building2Icon className={`shrink-0 ${size}`} />
        )
      }
    />
  );
}

/**
 * Tenant selection shared by every switcher surface (sidebar row on mobile,
 * nav rail mark on desktop): the visible orgs, the active one, and a select
 * handler that navigates to `/$org` and records the choice.
 */
export function useOrgSelection() {
  const navigate = useNavigate();
  const { orgs, available } = useOrgs();
  const activeOrg = useActiveOrg();

  const selectOrg = useCallback(
    (orgId: string) => {
      if (orgId === activeOrg?.id) {
        return;
      }
      persistLastPickedOrg(orgId);
      void navigate({ to: "/$org", params: { org: orgUrlSegment(orgId) } });
    },
    [activeOrg?.id, navigate],
  );

  return { orgs, activeOrg, available, selectOrg };
}

/**
 * The org list inside a switcher popup; shared by the sidebar and rail triggers.
 * Organizations served by a dedicated deployment follow as links that leave
 * this app for that deployment's own address.
 */
export function OrgMenuItems({
  orgs,
  activeOrgId,
  onSelect,
}: {
  readonly orgs: readonly OrgView[];
  readonly activeOrgId: string | null;
  readonly onSelect: (orgId: string) => void;
}) {
  const { elsewhere } = useOrgs();
  return (
    <>
      <OrgRadioItems orgs={orgs} activeOrgId={activeOrgId} onSelect={onSelect} />
      <ElsewhereOrgItems orgs={elsewhere} />
    </>
  );
}

export function ElsewhereOrgItems({ orgs }: { readonly orgs: readonly ElsewhereOrgView[] }) {
  if (orgs.length === 0) {
    return null;
  }
  return (
    <>
      <MenuSeparator />
      {orgs.map((org) => (
        <MenuItem
          key={org.id}
          className="h-8 min-h-8 gap-2 px-1 py-0 text-sm font-medium"
          title={`Opens ${new URL(org.url).host}`}
          onClick={() => window.location.assign(org.url)}
        >
          <OrgLogo org={{ ...org, role: null, lastChosen: false }} />
          <span className="min-w-0 flex-1 truncate text-sm">{org.name}</span>
          <ArrowUpRightIcon
            aria-label="Opens another address"
            className="size-3.5 shrink-0 opacity-60"
          />
        </MenuItem>
      ))}
    </>
  );
}

function OrgRadioItems({
  orgs,
  activeOrgId,
  onSelect,
}: {
  readonly orgs: readonly OrgView[];
  readonly activeOrgId: string | null;
  readonly onSelect: (orgId: string) => void;
}) {
  return (
    <MenuRadioGroup value={activeOrgId ?? ""} onValueChange={(value) => onSelect(value as string)}>
      {orgs.map((org) => (
        <MenuRadioItem
          key={org.id}
          value={org.id}
          closeOnClick
          className="h-8 min-h-8 px-1 py-0 text-sm font-medium [&>span:last-child]:flex [&>span:last-child]:min-w-0 [&>span:last-child]:items-center [&>span:last-child]:gap-2"
        >
          <OrgLogo org={org} />
          <span className="min-w-0 truncate text-sm">{org.name}</span>
        </MenuRadioItem>
      ))}
    </MenuRadioGroup>
  );
}

export default function OrgSwitcher() {
  const { orgs, activeOrg, available, selectOrg } = useOrgSelection();
  const [open, setOpen] = useState(false);

  if (!available) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      <Menu open={open} onOpenChange={setOpen}>
        <MenuTrigger
          render={
            <SidebarMenuButton
              aria-label="Switch organization"
              className="min-w-0 flex-1 ps-[calc(var(--sidebar-row-content-inset)-1px)] focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar"
            />
          }
        >
          <OrgLogo org={activeOrg ?? null} />
          <span className="min-w-0 flex-1 truncate">
            {activeOrg?.name ?? "Select organization"}
          </span>
          <ChevronDownIcon className="-mr-px size-4 shrink-0" />
        </MenuTrigger>
        <MenuPopup align="start" className="w-(--anchor-width)">
          <OrgMenuItems orgs={orgs} activeOrgId={activeOrg?.id ?? null} onSelect={selectOrg} />
        </MenuPopup>
      </Menu>
    </div>
  );
}
